<template>
  <!-- begin:: Header -->
  <header
    id="kt_header"
    ref="kt_header"
    class="header app-header"
    v-bind:class="headerClasses"
  >
    <div class="app-header__backdrop" aria-hidden="true"></div>
    <div class="app-header__inner">

      <!-- ─── LEFT: Active branch ───────────────────────────────── -->
      <div class="header-branch" aria-label="Cabang klinik aktif">
        <BranchSwitcher />
      </div>

      <div class="header-clock" aria-label="Tanggal dan waktu klinik">
        <span class="header-clock__date">{{ clinicDate }}</span>
        <span class="header-clock__time">
          {{ clinicTime }}
          <span class="header-clock__zone">{{ clinicTimezoneAbbreviation }}</span>
        </span>
      </div>

      <!-- ─── RIGHT: Topbar actions ────────────────────────────── -->
      <div class="header-actions">
        <!-- Header menu (if enabled) -->
        <div
          v-if="headerMenuEnabled"
          class="header-menu-wrapper header-menu-wrapper-left"
          ref="kt_header_menu_wrapper"
        >
          <div
            id="kt_header_menu"
            ref="kt_header_menu"
            class="header-menu header-menu-mobile"
            v-bind:class="headerMenuClasses"
          >
            <KTMenu></KTMenu>
          </div>
        </div>
        <!-- Topbar -->
        <KTTopbar></KTTopbar>
      </div>

    </div>
  </header>
  <!-- end:: Header -->
</template>

<script>
import { mapGetters } from "vuex";
import KTTopbar from "@/view/layout/header/Topbar.vue";
import BranchSwitcher from "@/view/layout/header/BranchSwitcher.vue";
import KTLayoutHeader from "@/assets/js/layout/base/header.js";
import KTLayoutHeaderMenu from "@/assets/js/layout/base/header-menu.js";
import KTMenu from "@/view/layout/header/Menu.vue";
import {
  clinicTimezoneInfo,
  formatClinicDate,
  formatClinicTime,
} from "@/core/services/clinic-time.service.js";

export default {
  name: "KTHeader",
  components: { BranchSwitcher, KTTopbar, KTMenu },
  data() {
    return {
      clinicClockNow: new Date(),
      clinicClockTimer: null,
    };
  },
  mounted() {
    KTLayoutHeader.init("kt_header", "kt_header_mobile");
    KTLayoutHeaderMenu.init(
      this.$refs["kt_header_menu"],
      this.$refs["kt_header_menu_wrapper"]
    );
    this.clinicClockTimer = window.setInterval(this.refreshClinicClock, 30000);
    this.$root.$on("configurationEditSuccessfully", this.refreshClinicClock);
  },
  beforeDestroy() {
    window.clearInterval(this.clinicClockTimer);
    this.$root.$off("configurationEditSuccessfully", this.refreshClinicClock);
  },
  computed: {
    ...mapGetters(["layoutConfig", "getClasses"]),
    headerMenuEnabled() {
      return !!this.layoutConfig("header.menu.self.display");
    },
    headerClasses() {
      const c = this.getClasses("header");
      return Array.isArray(c) ? c.join(" ") : null;
    },
    headerMenuClasses() {
      const c = this.getClasses("header_menu");
      return Array.isArray(c) ? c.join(" ") : null;
    },
    clinicDate() {
      return formatClinicDate(this.clinicClockNow, {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    clinicTime() {
      return formatClinicTime(this.clinicClockNow);
    },
    clinicTimezoneAbbreviation() {
      return clinicTimezoneInfo().label.split(" ")[0];
    },
  },
  methods: {
    refreshClinicClock() {
      this.clinicClockNow = new Date();
    },

  },
};
</script>

<style lang="scss" scoped>
/* ================================================================
   APP HEADER  –  Enterprise / Clean design
   Brand palette: var(--skin-deep) | var(--skin-primary) | var(--skin-secondary)
   ================================================================ */

/* ── CSS Variables ──────────────────────────────────────────── */
.app-header {
  /* Primary navy */
  --hc-navy:       var(--skin-deep);
  --hc-navy-mid:   var(--skin-primary);
  --hc-teal:       var(--skin-secondary);
  --hc-aqua:       var(--skin-secondary);

  /* Tints (opacity on white bg) */
  --hc-navy-10:    rgba(var(--skin-deep-rgb),  0.10);
  --hc-navy-06:    rgba(var(--skin-deep-rgb),  0.06);
  --hc-navy-15:    rgba(var(--skin-deep-rgb),  0.15);
  --hc-teal-10:    rgba(var(--skin-secondary-rgb), 0.12);

  /* Surface */
  --hc-white:      #ffffff;
  --hc-bg:         #F7F9FC;
  --hc-border:     #E4E9F0;
  --hc-muted:      rgba(237, 251, 255, 0.72);
  --hc-muted-icon: rgba(255, 255, 255, 0.72);
  --hc-header-text: #F7FCFF;
  --hc-header-border: rgba(255, 255, 255, 0.18);

  /* Radius */
  --hc-r-sm:  6px;
  --hc-r-md:  10px;
  --hc-r-pill: 50rem;

  /* Spacing */
  --hc-gap: 1rem;
  --hc-px:  1.25rem;
}

/* ── Shell ──────────────────────────────────────────────────── */
.app-header {
  display: flex;
  align-items: stretch;
  isolation: isolate;
  background:
    radial-gradient(circle at 13% -85%, rgba(var(--skin-secondary-rgb), 0.30) 0 62px, transparent 63px),
    radial-gradient(circle at 72% 145%, rgba(var(--skin-secondary-rgb), 0.22) 0 54px, transparent 55px),
    radial-gradient(circle at 93% -55%, rgba(255, 255, 255, 0.15) 0 38px, transparent 39px),
    linear-gradient(112deg, var(--skin-deep) 0%, var(--skin-primary) 48%, var(--skin-secondary) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  /* The `left` / `right` positioning is controlled by the KT layout engine
     via header-fixed + aside-enabled classes on <body>. We must NOT add
     width:100% here – that would break the sliding mechanism. */

  /* Subtle bottom separator gradient for depth */
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 8px 24px var(--skin-shadow);
}

/* ── Inner row ──────────────────────────────────────────────── */
.app-header__backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 13% -85%, rgba(var(--skin-secondary-rgb), 0.34) 0 62px, transparent 63px),
    radial-gradient(circle at 72% 145%, rgba(var(--skin-secondary-rgb), 0.24) 0 54px, transparent 55px),
    radial-gradient(circle at 93% -55%, rgba(255, 255, 255, 0.16) 0 38px, transparent 39px),
    linear-gradient(112deg, var(--skin-deep) 0%, var(--skin-primary) 48%, var(--skin-secondary) 100%) !important;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 8px 24px var(--skin-shadow);
}

.app-header__inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 var(--hc-px);
  gap: var(--hc-gap);
  /* Do NOT use overflow:hidden – it clips position:absolute children
     (search icon, badge counters). Use min-width:0 on flex children instead. */
}

/* ================================================================
   ACTIVE BRANCH (LEFT)
   ================================================================ */
.header-branch {
  display: flex;
  align-items: center;
  flex: 0 1 330px;
  max-width: 330px;
  min-width: 0;
  padding-right: var(--hc-gap);
  border-right: 1px solid var(--hc-header-border);
}

/* Icon container */
.brand-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--hc-r-md);
  background: linear-gradient(135deg, var(--hc-navy-10), var(--hc-teal-10));
  
  
  
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.brand-svg {
  width: 20px;
  height: 20px;
  color: var(--hc-header-text);
}

.brand-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: calc(var(--hc-r-md) - 1px);
}

/* Text block */
.brand-info {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  min-width: 0;
}

.brand-name-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  line-height: 1;
}

.brand-name {
  max-width: none;
  overflow: visible;
  font-weight: 700;
  font-size: clamp(0.95rem, 1.12vw, 1.2rem);
  letter-spacing: 0.2px;
  color: var(--hc-navy);
  white-space: nowrap;
  text-overflow: clip;
  text-transform: uppercase;
}

.brand-sub {
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--hc-muted);
  white-space: nowrap;
  margin-top: 2px;
  letter-spacing: 0.15px;
}

/* Status badge */
.brand-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: var(--hc-r-pill);
  background: rgba(255, 255, 255, 0.12);
  color: #DFFFF9;
  border: 1px solid rgba(255, 255, 255, 0.24);
  box-shadow: 0 3px 10px rgba(4, 35, 60, 0.1);
}

.brand-badge__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #7FF4D9;
  box-shadow: 0 0 0 3px rgba(127, 244, 217, 0.14);
  /* subtle pulse */
  animation: pulse-dot 2.4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
}

/* ================================================================
   ACTIONS (RIGHT)
   ================================================================ */
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  margin-left: auto;
  padding-left: var(--hc-gap);
  /* Left separator */
  border-left: 1px solid var(--hc-header-border);
}

.header-clock {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  min-width: 94px;
  margin-left: 0;
  padding: 0 0.875rem;
  color: rgba(247, 252, 255, 0.82);
  line-height: 1.15;
  text-align: left;
  white-space: nowrap;
}

.header-clock__date {
  font-size: 0.77rem;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.header-clock__time {
  margin-top: 3px;
  color: #ffffff;
  font-size: 0.91rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.header-clock__zone {
  color: #ffffff !important;
  font-size: 0.67rem;
  font-weight: 600;
}

@media (max-width: 1420px) {
  .header-clock {
    display: none;
  }
}

@media (max-width: 1180px) {
  .app-header__inner {
    --hc-gap: 0.65rem;
    --hc-px: 0.85rem;
  }

  .header-branch {
    flex-basis: 270px;
    padding-right: 0.65rem;
  }

  .brand-sub {
    display: none;
  }
}

/* ================================================================
   TOPBAR ICON — hapus background biru di belakang icon
   ================================================================ */
:deep(.topbar .btn.btn-icon),
:deep(.topbar .btn.btn-icon:focus),
:deep(.topbar .btn.btn-icon:active),
:deep(.topbar .btn.btn-icon.active),
:deep(.topbar .show .btn.btn-icon.btn-dropdown),
:deep(.topbar .btn.btn-clean) {
  color: var(--hc-header-text) !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  box-shadow: 0 4px 12px rgba(5, 38, 67, 0.1) !important;
}

:deep(.topbar .btn.btn-icon:hover),
:deep(.topbar .btn.btn-clean:hover) {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.34) !important;
  box-shadow: 0 6px 16px rgba(5, 38, 67, 0.16) !important;
  transform: translateY(-1px);
}

:deep(.header-menu .menu-link),
:deep(.header-menu .menu-text) {
  color: rgba(255, 255, 255, 0.84) !important;
}

:deep(.header-menu .menu-link:hover),
:deep(.header-menu .menu-item-active > .menu-link) {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.14) !important;
  border-radius: var(--hc-r-md);
}

@media (prefers-reduced-motion: reduce) {
  .brand-badge__dot {
    animation: none;
  }
}
</style>
